import { ContentContainer } from "@Components/forecast/ContentContainer";
import { Day } from "@Components/forecast/daily/Day";
import {
  useForecastAppContext,
  useForecastAppDispatcher,
} from "@Context/ForecastAppContext/ForecastAppContext";
import { useTranslations } from "@Context/TranslationContext/TranslationContext";
import { useAnimatedWeatherIcons } from "@Hooks/useAnimatedWeatherIcons";
import { useQueryParam } from "@Hooks/useQueryParam";
import { DayForecastInterval } from "@MeteoApi/models/DayForecastInterval";
import { formatAsIsoStyleDate, formatAsWeekday } from "@Utils/date";
import { getValidForecastDays } from "@Utils/interval";
import { scrollElementToViewport } from "@Utils/scrolling";
import { trackDaySelection } from "@Utils/tracking";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";

export const NavigationContainer = () => {
  const dispatch = useForecastAppDispatcher();
  const { activeDate, forecastPointWeek, symbolTexts } =
    useForecastAppContext();
  const { updateValue: updateDateQueryParamValue } = useQueryParam("date");
  const { currentLocale } = useTranslations();
  const { enabled: isWeatherIconAnimated } = useAnimatedWeatherIcons();

  const [days, setDays] = useState<DayForecastInterval[]>([]);
  const dayRefs = useRef<{ [key: string]: RefObject<HTMLLIElement> }>({});
  const [timezone, setTimezone] = useState<string>("Europe/Zurich");
  const keyboardNavigationRef = useRef(false);

  const handleDaySelect = (date: string, index: number) => {
    if (dayRefs?.current[date]?.current) {
      const element = dayRefs?.current[date]?.current;

      if (element) {
        scrollElementToViewport(element, 16, keyboardNavigationRef);
      }

      dispatch({
        type: "set-active-date",
        data: {
          value: date,
          source: "user-day-select",
        },
      });

      trackDaySelection(index, "click");
    }
  };

  const isActive = useCallback(
    (dateOnly: string, index: number): boolean => {
      if (!activeDate) {
        return false;
      }
      // Check if the active date is available in the data
      const hasDayWithDate = days.some((day) =>
        day.date_time.startsWith(activeDate.value),
      );

      // If the active date matches the current date being iterate on
      if (hasDayWithDate) {
        return dateOnly === activeDate.value;
      }

      // Otherwise, if it's the first day
      return index === 0;
    },
    [activeDate, days],
  );

  const getRainAmountsInSegmentsForDate = (date: string): number[] => {
    if (!forecastPointWeek) {
      return [];
    }

    const { three_hours = [] } = forecastPointWeek;

    // Find all three hour intervals (segments) of the given date
    const threeHourIntervalsForDate = three_hours.filter((interval) =>
      interval.date_time.startsWith(date),
    );

    return threeHourIntervalsForDate.map((interval) => interval.RRR_MM);
  };

  useEffect(() => {
    if (!forecastPointWeek || Object.keys(forecastPointWeek).length === 0) {
      return;
    }

    setDays(getValidForecastDays(forecastPointWeek, dayRefs));
    setTimezone(forecastPointWeek.geolocation.timezone);
  }, [forecastPointWeek]);

  useEffect(() => {
    if (activeDate) {
      updateDateQueryParamValue(activeDate.value);
    }
  }, [activeDate, updateDateQueryParamValue]);

  return (
    <div className="m:border-0 m:border-b-2 m:border-solid m:border-warmgrey-100 m:bg-warmgrey-25 m:font-sans m:text-blue-950 m:dark:border-blue-850 m:dark:bg-blue-900 m:dark:text-neutral-white">
      <ContentContainer xOverflowAuto>
        <div className="m:overflow-x-auto m:before:right-full m:after:left-full m:md:px-8 m:lg:before:absolute m:lg:before:bottom-0 m:lg:before:block m:lg:before:h-[252px] m:lg:before:content-[''] m:lg:after:absolute m:lg:after:bottom-0 m:lg:after:block m:lg:after:h-[252px] m:lg:after:content-[''] m:xl:px-10">
          <ul
            className="m:scrollbar-hidden m:flex m:list-none m:overflow-x-auto m:font-light"
            data-testid="day-navigation">
            {days.map((day, index) => {
              const localDateTime = day.date_time;
              const localDateOnly = formatAsIsoStyleDate(localDateTime);

              const rainAmountInSegments =
                getRainAmountsInSegmentsForDate(localDateOnly);

              const label = formatAsWeekday(
                localDateTime,
                timezone,
                currentLocale,
              );
              return (
                <li
                  key={localDateOnly}
                  className="m:m-0 m:list-item m:h-[214px] m:max-w-[117px] m:min-w-[102px] m:shrink-0 m:grow m:basis-[28%] m:p-0 m:antialiased m:transition-[height] m:delay-0 m:duration-[0.2s] m:ease-in-out m:before:absolute m:before:content-[''] m:xs:shrink-0 m:xs:grow-0 m:xs:basis-[104px] m:md:h-[242px] m:md:max-w-[160px] m:md:shrink-0 m:md:grow m:md:basis-[104px] m:lg:h-[252px] m:lg:max-w-[200px] m:lg:shrink m:lg:grow-0 m:lg:basis-[200px]"
                  data-date={localDateOnly}
                  data-active={isActive(localDateOnly, index)}
                  ref={dayRefs.current[localDateOnly]}
                  data-testid="day-navigation-item">
                  <Day
                    label={label}
                    day={day}
                    symbolText={symbolTexts[day.symbol24_code]}
                    rainAmountInSegments={rainAmountInSegments}
                    isActive={isActive(localDateOnly, index)}
                    isIconAnimated={isWeatherIconAnimated}
                    onSelect={() => handleDaySelect(localDateOnly, index)}
                    keyboardNavigationRef={keyboardNavigationRef}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      </ContentContainer>
    </div>
  );
};
